import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { AppService } from "@App/app.service";
import { ObjectTagsComponent } from "@Components/object-tags/object-tags.component";
import { AccountSettingsDTO } from "@DTOs";
import { AccountSettings } from "@Enums";
import { Account, Client, UWResponse } from "@Models";
import { AccountSettingsDataService } from "@Services";
import { SharedModule } from "@Shared/shared.module";
import { TimeUtils, Utils } from "@Utils";
import { catchError, map, of } from "rxjs";
import { SubSink } from "subsink";

interface AutomatedMappingToggleSetting {
    accountSettingName: AccountSettings;
    formControlName: string;
    title: string;
    description: string;
    defaultValue: boolean;
    currentValue?: boolean;
    initialValue?: boolean;
    accountSettingId?: string;
}

@Component({
    selector: 'workbenchv2-automated-mapping-config',
    templateUrl: './workbenchv2-automated-mapping-config.component.html',
    imports: [SharedModule, ObjectTagsComponent]
})
export default class WorkbenchV2AutomatedMappingConfigComponent implements OnInit, OnDestroy {
    @Input() client: Client;
    @Input() account: Account;

    accountSettings: AccountSettingsDTO[] = [];
    accountSettingsLoaded = false;
    errorMessage = '';
    lastUpdated: Date;
    attachmentTags: string[] = [];
    orphanAttachmentTags: string[] = [];

    requirementFirstControlName = 'requirementFirst';
    attachmentFirstControlName = 'attachmentFirst';

    toggleSettings: AutomatedMappingToggleSetting[] = [
        {
            accountSettingName: AccountSettings.UWWorkbenchRequirementFirst,
            formControlName: this.requirementFirstControlName,
            title: 'Requirement First',
            description: 'When enabled, attaching a tagged document maps it to all matching non-closed requirements. When disabled, no document-to-requirement mapping occurs when matching documents are added.',
            defaultValue: true,
            currentValue: true,
            initialValue: true
        },
        {
            accountSettingName: AccountSettings.UWWorkbenchAttachmentFirst,
            formControlName: this.attachmentFirstControlName,
            title: 'Attachment First',
            description: 'When enabled, adding a tagged requirement maps the most recent unassigned matching document. When disabled, no document-to-requirement mapping occurs when matching requirements are added.',
            defaultValue: false,
            currentValue: false,
            initialValue: false
        }
    ];

    subs = new SubSink();

    constructor(
        private readonly appService: AppService,
        private readonly accountSettingsDataService: AccountSettingsDataService,
    ) { }

    ngOnInit() {
        this.subscribeToAccountSettings();
    }

    private subscribeToAccountSettings() {
        this.subs.add(this.accountSettingsDataService.loadAccountSettings(this.account.id).subscribe(accountSettings => {
            this.accountSettings = accountSettings;
            this.setForm();
            this.accountSettingsLoaded = true;
        }));
    }

    private setForm() {
        const settingsDates: Date[] = [];
        this.toggleSettings.forEach(setting => {
            const existingSetting = this.accountSettings.find(x => x.name === setting.accountSettingName);
            const currentValue = existingSetting ? existingSetting.value?.toLowerCase() === 'true' : setting.defaultValue;

            setting.currentValue = currentValue;
            setting.initialValue = currentValue;
            setting.accountSettingId = existingSetting?.id;

            const date = TimeUtils.convertToDateCst(existingSetting?.lastModifiedDate ?? existingSetting?.creationDate);
            if (date) {
                settingsDates.push(date);
            }
        });

        const requirementExclusionSetting = this.getAccountSetting(AccountSettings.requirementTagExclusions);
        const orphanRequirementExclusionSetting = this.getAccountSetting(AccountSettings.orphanRequirementTagExclusions);
        this.attachmentTags = requirementExclusionSetting?.value ? requirementExclusionSetting.value.split(',') : [];
        this.orphanAttachmentTags = orphanRequirementExclusionSetting?.value ? orphanRequirementExclusionSetting.value.split(',') : [];

        const requirementDate = TimeUtils.convertToDateCst(requirementExclusionSetting?.lastModifiedDate ?? requirementExclusionSetting?.creationDate);
        const orphanDate = TimeUtils.convertToDateCst(orphanRequirementExclusionSetting?.lastModifiedDate ?? orphanRequirementExclusionSetting?.creationDate);
        if (requirementDate) {
            settingsDates.push(requirementDate);
        }
        if (orphanDate) {
            settingsDates.push(orphanDate);
        }

        this.lastUpdated = settingsDates.length > 0
            ? settingsDates.sort((a, b) => b.getTime() - a.getTime())[0]
            : null;
    }

    private getAccountSetting(accountSetting: AccountSettings) {
        return this.accountSettings.find(x => x.name === accountSetting) ?? null;
    }

    onToggleFeature(toggleSetting: AutomatedMappingToggleSetting, checked: boolean) {
        toggleSetting.currentValue = checked;
        this.saveToggleSetting(toggleSetting).subscribe();
    }

    saveRequirementTags(tags: string[]) {
        this.attachmentTags = tags;
        this.saveTags(AccountSettings.requirementTagExclusions, this.attachmentTags).subscribe();
    }

    saveOrphanTags(tags: string[]) {
        this.orphanAttachmentTags = tags;
        this.saveTags(AccountSettings.orphanRequirementTagExclusions, this.orphanAttachmentTags).subscribe();
    }

    private saveToggleSetting(toggleSetting: AutomatedMappingToggleSetting) {
        const updateAccountSetting: AccountSettingsDTO = {
            id: toggleSetting.accountSettingId ?? Utils.emptyGuid,
            accountId: this.account.id,
            name: toggleSetting.accountSettingName,
            value: toggleSetting.currentValue.toString()
        };

        return this.appService.postData<UWResponse<AccountSettingsDTO[]>>('AccountSettings', [updateAccountSetting]).pipe(
            map(() => {
                toggleSetting.initialValue = toggleSetting.currentValue;
                this.accountSettingsDataService.reloadAccountSettings(this.account.id).subscribe();
                return true;
            }),
            catchError((err: Error) => {
                this.errorMessage = err.message;
                toggleSetting.currentValue = toggleSetting.initialValue;
                return of(false);
            }),
        );
    }

    private saveTags(accountSetting: AccountSettings, tags: string[]) {
        const existingAccountSetting = this.accountSettings.find(x => x.name === accountSetting);
        const updateAccountSetting: AccountSettingsDTO = {
            id: existingAccountSetting?.id ?? Utils.emptyGuid,
            accountId: this.account.id,
            name: accountSetting,
            value: tags.join(',')
        };

        return this.appService.postData<UWResponse<AccountSettingsDTO[]>>('AccountSettings', [updateAccountSetting]).pipe(
            map(() => {
                this.accountSettingsDataService.reloadAccountSettings(this.account.id).subscribe();
                return true;
            }),
            catchError((err: Error) => {
                this.errorMessage = err.message;
                return of(false);
            }),
        );
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }
}
